import type { ReactNode } from 'react'
import { AlertTriangle, Check, CircleDot, Loader2, X } from 'lucide-react'
import type { UiState } from '../../types'

/* ============================================================================
 * СИСТЕМА СОСТОЯНИЙ ИНТЕРФЕЙСА
 *
 * Канонический набор CSS-классов (theme.css → @layer components):
 *   active, selected, critical-error, warning, pending, approved
 *
 * Компонент StateBadge — единая точка отображения состояния: цвет, иконка
 * и текст берутся из состояния, а не из разметки на каждом элементе.
 * ========================================================================= */

/** Состояние → CSS-класс */
export function stateClass(state: UiState | null | undefined): string {
  return state ? `ut-state-${state}` : ''
}

/** Состояние → человекочитаемая подпись */
export const STATE_LABEL: Record<UiState, string> = {
  active: 'Активно',
  selected: 'Выбрано',
  'critical-error': 'Критическая ошибка',
  warning: 'Предупреждение',
  pending: 'Ожидание',
  approved: 'Согласовано',
}

/** Состояние → иконка */
function StateIcon({ state }: { state: UiState }) {
  switch (state) {
    case 'critical-error':
      return <X size={11} strokeWidth={2.5} aria-hidden />
    case 'warning':
      return <AlertTriangle size={11} strokeWidth={2.5} aria-hidden />
    case 'approved':
      return <Check size={11} strokeWidth={2.5} aria-hidden />
    case 'pending':
      return <Loader2 size={11} strokeWidth={2.5} className="ut-spin" aria-hidden />
    case 'selected':
      return <CircleDot size={11} strokeWidth={2.5} aria-hidden />
    case 'active':
    default:
      return <CircleDot size={11} strokeWidth={2.5} aria-hidden />
  }
}

/**
 * Бейдж состояния.
 *
 * @param state одно из канонических состояний
 * @param children текст (по умолчанию — STATE_LABEL[state])
 * @param pulsing пульсировать (используется для нарушителей)
 */
export function StateBadge({
  state,
  children,
  pulsing = false,
  title,
}: {
  state: UiState
  children?: ReactNode
  pulsing?: boolean
  title?: string
}) {
  return (
    <span
      className={`ut-badge ${stateClass(state)} ${pulsing ? 'ut-pulsing' : ''}`}
      title={title ?? STATE_LABEL[state]}
      data-state={state}
    >
      <StateIcon state={state} />
      {children ?? STATE_LABEL[state]}
    </span>
  )
}

/**
 * Контейнер с состоянием — применяет к элементу классы состояния
 * (цвет рамки, фон, индикатор слева).
 */
export function StatedBox({
  state,
  children,
  className = '',
  showBar = true,
}: {
  state: UiState
  children: ReactNode
  className?: string
  showBar?: boolean
}) {
  return (
    <div className={`ut-stated ${stateClass(state)} ${className}`} data-state={state}>
      {showBar && <span className="ut-state-bar absolute left-0 top-0 h-full w-[3px]" aria-hidden />}
      {children}
    </div>
  )
}

/** Точка-индикатор состояния.
 *  pulsing — пульсация, применяется для нарушителей и критических событий. */
export function StateDot({
  state,
  className = '',
  pulsing = false,
}: {
  state: UiState
  className?: string
  pulsing?: boolean
}) {
  return (
    <span
      className={`ut-state-dot ${stateClass(state)} ${pulsing ? 'ut-pulsing' : ''} inline-block h-2 w-2 shrink-0 rounded-full ${className}`}
      data-state={state}
      aria-label={STATE_LABEL[state]}
      role="img"
    />
  )
}
